import { lazy, Suspense, useState, type FormEvent } from 'react';
import { ArrowUpRight, ShieldCheck } from 'lucide-react';
import StorePage from './StorePage';
import { affiliateRequest } from '@/services/affiliates';
import { store } from '@/services/store';
import './affiliates.css';
const AffiliateDashboard = lazy(() => import('./AffiliateDashboard'));

type Dashboard = {
  affiliate: { code: string; name: string; active: number }; enabled: boolean; rate: number;
  earned: number; paid: number; balance: number; lastSync?: number | null; counts?: { total: number; confirmed: number }; clicks?: { total: number; last30: number };
  orders: { order_id: string; rate: number; amount: number; state: string; created: number; synced: number | null }[];
};
const demoOrders = Array.from({length: 28}, (_, i) => ({
  order_id: String(1082 + i), rate: 1000,
  amount: i % 9 === 0 || i % 11 === 0 ? 0 : 1290 + (i * 713) % 4200,
  state: i % 11 === 0 ? 'pending' : i % 9 === 0 ? 'cancelled' : 'confirmed',
  created: Math.floor(new Date(2026, 8, 1 + Math.floor(i * 21 / 28), 12).getTime() / 1000),
  synced: Math.floor(new Date(2026, 8, 22, 12).getTime() / 1000),
})).reverse();
const demoEarned = demoOrders.reduce((sum, order) => sum + order.amount, 0);
const demoDashboard: Dashboard = {
  affiliate: { code: 'ana-demo', name: 'Ana', active: 1 },
  enabled: true, rate: 1000, earned: demoEarned, paid: 25000, balance: demoEarned - 25000,
  orders: demoOrders, lastSync: Math.floor(Date.now() / 1000) - 7 * 60,
};

export default function Affiliates() {
  const demo = import.meta.env.DEV && new URLSearchParams(window.location.search).get('demo') === '1';
  const [access, setAccess] = useState('');
  const [data, setData] = useState<Dashboard | null>(demo ? demoDashboard : null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function login(event?: FormEvent) {
    if (demo) { event?.preventDefault(); setData(demoDashboard); setError(''); return; }
    event?.preventDefault(); setError(''); setBusy(true);
    try { setData(await affiliateRequest({ action: 'dashboard', access: access.trim() })); }
    catch (e) { setError(e instanceof Error ? e.message : 'Não foi possível entrar.'); }
    finally { setBusy(false); }
  }
  if (data) return <Suspense fallback={<div role="status" style={{padding:40}}>Carregando seu painel…</div>}><AffiliateDashboard data={data} demo={demo} busy={busy} error={error} refresh={()=>void login()} logout={()=>{setData(null);setAccess('');setError('');}}/></Suspense>;
  return <StorePage title="Programa de afiliados" subtitle="Boas indicações merecem ser valorizadas.">
    {demo && <button className="affiliate-cta" onClick={()=>setData(demoDashboard)}>Abrir dashboard</button>}
    <div className="affiliate-intro">
      <section className="affiliate-invitation">
        <span className="affiliate-kicker">COMPARTILHE O SEU ESTILO</span>
        <h2>Seu bom gosto.<br />Novas possibilidades.</h2>
        <p>Indique a Rica Tanto com seu link exclusivo e acompanhe as comissões das compras realizadas por sua indicação.</p>
        <a className="affiliate-cta" href={`${store.whatsapp}?text=${encodeURIComponent('Olá! Gostaria de participar do programa de afiliados da Rica Tanto.')}`} target="_blank" rel="noopener noreferrer">Quero participar <ArrowUpRight size={20} /></a>
        <span className="affiliate-small">Participação sujeita à aprovação da loja e à ativação do programa.</span>
      </section>
      <section className="affiliate-login">
        <ShieldCheck size={30} strokeWidth={1.4} /><h2>Seu espaço de afiliado</h2>
        <p>Já faz parte? Use o código privado que recebeu da loja para acompanhar seus resultados.</p>
        <form onSubmit={login}>
          <label htmlFor="affiliate-access">Código de acesso privado</label>
          <input id="affiliate-access" type="password" autoComplete="off" required minLength={64} maxLength={64} value={access} onChange={e => setAccess(e.target.value)} placeholder="Cole seu código de acesso" />
          <button className="affiliate-cta" disabled={busy}>{busy ? 'Acessando…' : 'Acessar meu painel'} <ArrowUpRight size={18} /></button>
        </form>
        {error && <p role="alert" className="affiliate-error">{error}</p>}
        <a className="affiliate-help" href={store.whatsapp} target="_blank" rel="noopener noreferrer">Preciso de ajuda com meu acesso</a>
      </section>
    </div>
    <section className="affiliate-how"><span className="affiliate-kicker">SIMPLES DE COMPARTILHAR</span><h2>Uma indicação. Três passos.</h2><div>{[
      ['01', 'Receba seu link', 'Solicite sua participação à loja. Após a aprovação, você recebe seu link e um acesso privado.'],
      ['02', 'Compartilhe suas escolhas', 'A indicação vale por até 30 dias, neste navegador. Se outro link de afiliado for acessado, a indicação mais recente será considerada.'],
      ['03', 'Acompanhe seus resultados', 'A comissão considera produtos após descontos, sem frete, em pedidos pagos. Repasses são combinados e conferidos com a loja.'],
    ].map(([number, title, text]) => <article key={number}><span>{number}</span><h3>{title}</h3><p>{text}</p></article>)}</div></section>
  </StorePage>;
}
